import type { CSSProperties, ReactNode } from "react";
import { useIsBelowLg } from "../../hooks/useIsBelowLg";
import { useReimbursementFormSticky } from "../../hooks/useReimbursementFormSticky";
import { ReimbursementFormProgressDrawer } from "./ReimbursementFormProgressDrawer";

export interface ReimbursementFormStickyLayoutProps {
  statusBar: ReactNode;
  children: ReactNode;
}

const BOTTOM_ANCHOR_STYLE: CSSProperties = {
  position: "absolute",
  bottom: 0,
  left: 0,
  width: "100%",
  zIndex: 30,
};

/**
 * 20/80 flex layout for the reimbursement form. Uses scroll-driven positioning
 * (natural → pinned → bottom) so the status bar stays visible while scrolling
 * the form, then anchors to the bottom of the boundary when scrolled past.
 */
export function ReimbursementFormStickyLayout({
  statusBar,
  children,
}: ReimbursementFormStickyLayoutProps) {
  const isBelowLg = useIsBelowLg();
  const { boundaryRef, columnRef, asideRef, mode, asideHeight, fixedStyle } =
    useReimbursementFormSticky(!isBelowLg);

  const asideStyle: CSSProperties =
    fixedStyle.position === "fixed"
      ? fixedStyle
      : mode === "bottom"
        ? BOTTOM_ANCHOR_STYLE
        : {};

  const showPlaceholder = fixedStyle.position === "fixed" && asideHeight > 0;

  if (isBelowLg) {
    return (
      <div
        className="min-w-0"
        data-testid="reimbursement-form-sticky-boundary"
        data-layout="drawer"
      >
        <div className="min-w-0">{children}</div>
        <ReimbursementFormProgressDrawer statusBar={statusBar} />
      </div>
    );
  }

  return (
    <div
      ref={boundaryRef}
      className="flex flex-row items-stretch gap-4"
      data-testid="reimbursement-form-sticky-boundary"
      data-layout="sticky"
    >
      <div
        ref={columnRef}
        className="relative w-1/5 shrink-0 self-stretch"
        data-testid="reimbursement-form-sticky-column"
      >
        {showPlaceholder ? (
          <div
            aria-hidden
            data-testid="reimbursement-form-sticky-placeholder"
            style={{ height: asideHeight }}
          />
        ) : null}
        <aside
          ref={asideRef}
          style={asideStyle}
          className="z-30 w-full"
          data-testid="reimbursement-form-sticky-aside"
          data-sticky-mode={mode}
        >
          <div
            className="max-h-[calc(100dvh-2rem)] overflow-y-auto"
            data-testid="reimbursement-form-sticky-aside-scroll"
          >
            {statusBar}
          </div>
        </aside>
      </div>
      <div className="min-w-0 w-4/5 flex-1">{children}</div>
    </div>
  );
}
